import { IconFont, Tooltip } from '@byecode/ui'
import type {
    ArgumentsSchema,
    AvailableFieldBlockInfo,
    AvailableFilterBlockInfo,
    BlockVisibilityProtocol,
    DataSourceAbstract,
    PageVariableSchema
} from '@lighthouse/core'
import { isEmpty } from 'rambda'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import type { DeptOption, RoleOption, UserOption } from '../../types'
import type { CustomViewVisibleData } from '../../utils'
import { filterToString } from '../../utils'

const SCxTooltipContainer = styled.div`
    padding: 0 4px;
    color: var(--color-white);
    background: var(--color-gray-900);
`

const SCxVisibleContainer = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 100;
    height: 18px;
    border-radius: 3px;
    background-color: #1b1855;
`

const SCxVisibleWrapper = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
`

const SCxText = styled.p`
    white-space: pre;
`

export interface VisibleDomProps {
    id?: string
    customViewData?: CustomViewVisibleData
    dataSourceList: DataSourceAbstract[]
    filter?: BlockVisibilityProtocol
    personOptions: UserOption[]
    roleOptions: RoleOption[]
    departmentOptions: DeptOption[]
    availableFieldBlocksInfo: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo: AvailableFilterBlockInfo[]
    pageVariableSchemas?: PageVariableSchema[]
    pagePathParameter?: ArgumentsSchema[]
    pageQueryParameter?: ArgumentsSchema[]
}

export const VisibleDom: React.FC<VisibleDomProps> = ({
    id,
    customViewData,
    dataSourceList,
    filter,
    personOptions,
    roleOptions,
    departmentOptions,
    availableFieldBlocksInfo,
    pageVariableSchemas,
    pagePathParameter,
    pageQueryParameter,
    availableFilterBlocksInfo
}) => {
    const title = useMemo(
        () =>
            filter?.visible === 'FILTER' && filter.visibilityFilter
                ? filterToString({
                      customViewData,
                      dataSourceList,
                      filter: filter.visibilityFilter,
                      personOptions,
                      roleOptions,
                      departmentOptions,
                      availableFieldBlocksInfo,
                      pageVariableSchemas,
                      pagePathParameter,
                      pageQueryParameter,
                      availableFilterBlocksInfo
                  })
                : undefined,
        [
            customViewData,
            dataSourceList,
            departmentOptions,
            availableFieldBlocksInfo,
            filter?.visibilityFilter,
            filter?.visible,
            availableFilterBlocksInfo,
            pagePathParameter,
            pageQueryParameter,
            pageVariableSchemas,
            personOptions,
            roleOptions
        ]
    )

    const filterLabel = useMemo(
        () => (
            <SCxTooltipContainer>
                <SCxText>符合以下条件时显示该组件：</SCxText>
                {title && title.map((str, index) => <SCxText key={index}>{str}</SCxText>)}
            </SCxTooltipContainer>
        ),
        [title]
    )

    const isFilterVisible = useMemo(() => {
        return title && !isEmpty(title)
    }, [title])

    return (
        <SCxVisibleContainer>
            {isFilterVisible && (
                <Tooltip title={filterLabel}>
                    <SCxVisibleWrapper>
                        <IconFont fill="white" size={14} type="EyeOutline" />
                    </SCxVisibleWrapper>
                </Tooltip>
            )}
        </SCxVisibleContainer>
    )
}
